import NumberInput from "../../components/NumberInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  addStockTransfer,
  cancelStockTransfer,
  fetchStockTransferOptions,
  fetchStockTransfers,
  getStockTransferById,
  receiveStockTransfer,
  shipStockTransfer,
} from "../../actions/stockTransferAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import ItemAvatar from "../../components/ItemAvatar";
import { getActiveOutletId } from "../../utils/helpers/outletContext";
import { FormValidationNotice, RequiredMark } from "../../components/FormValidation";
import AkasSelect from "../../components/AkasSelect";
import "./stock-transfer.css";
import { hasAnyPermission } from "../../utils/helpers/permissions";

const STATUS = {
  draft: { label: "Draft", className: "bg-secondary-lt" },
  shipped: { label: "Dalam pengiriman", className: "bg-azure-lt" },
  received: { label: "Diterima", className: "bg-green-lt" },
  cancelled: { label: "Dibatalkan", className: "bg-red-lt" },
};

const EMPTY_LINE = { item_type: "material", item_id: "", qty: 1 };
const createFormState = () => ({
  destination_outlet_id: "",
  note: "",
  idempotency_key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
  details: [{ ...EMPTY_LINE }],
});

const formatQuantity = (value) => Number(value || 0).toLocaleString("id-ID", {
  maximumFractionDigits: 4,
});

const StockTransferPage = (props) => {
  const {
    dataStockTransfers,
    loading,
    actionLoading,
    fetchStockTransfers,
    getStockTransferById,
    fetchStockTransferOptions,
    addStockTransfer,
    shipStockTransfer,
    receiveStockTransfer,
    cancelStockTransfer,
    userProfile,
  } = props;
  const canProcess = hasAnyPermission(userProfile, ["process-stock-transfers"]);
  const activeOutletId = getActiveOutletId();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [form, setForm] = useState(createFormState);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const rows = useMemo(() => dataStockTransfers?.data || [], [dataStockTransfers]);
  const meta = dataStockTransfers || {};

  const load = useCallback(
    () => fetchStockTransfers({ status, search, limit: 100 }),
    [fetchStockTransfers, search, status],
  );

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    setShowForm(true);
    setValidationAttempted(false);
    setOptionsLoading(true);
    setForm(createFormState());
    const options = await fetchStockTransferOptions(activeOutletId);
    if (options) {
      setOutlets(options.outlets);
      setProducts(options.products.filter((product) => !product.inventory_mode || product.inventory_mode === "stocked"));
      setMaterials(options.materials);
    } else {
      setShowForm(false);
    }
    setOptionsLoading(false);
  };

  const itemOptions = (type) => type === "material" ? materials : products;
  const selectedItem = (line) => itemOptions(line.item_type)
    .find((item) => Number(item.id) === Number(line.item_id));
  const updateLine = (index, key, value) => setForm((current) => ({
    ...current,
    details: current.details.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value, ...(key === "item_type" ? { item_id: "" } : {}) } : line),
  }));
  const addLine = () => setForm((current) => ({ ...current, details: [...current.details, { ...EMPTY_LINE }] }));
  const removeLine = (index) => setForm((current) => ({ ...current, details: current.details.filter((_, lineIndex) => lineIndex !== index) }));

  const save = async (event) => {
    event.preventDefault();
    setValidationAttempted(true);
    const invalid = !form.destination_outlet_id || !form.details.length || form.details.some((line) => {
      const item = selectedItem(line);
      const quantity = Number(line.qty);
      return !line.item_id || !Number.isFinite(quantity) || quantity <= 0 || quantity > Number(item?.stock || 0) ||
        (line.item_type === "product" && !Number.isInteger(quantity));
    });
    if (invalid) return;
    const result = await addStockTransfer({
      ...form,
      details: form.details.map((line) => ({
        item_type: line.item_type,
        item_id: Number(line.item_id),
        qty: Number(line.qty),
      })),
    });
    if (result) {
      setShowForm(false);
      await load();
    }
  };

  const openDetail = async (id) => {
    const data = await getStockTransferById(id);
    setDetail(data);
  };

  const transition = async () => {
    if (!pendingTransition) return;
    if (pendingTransition.action !== "cancel" && !canProcess) return false;
    const actions = {
      ship: shipStockTransfer,
      receive: receiveStockTransfer,
      cancel: cancelStockTransfer,
    };
    const result = await actions[pendingTransition.action]?.(pendingTransition.transfer.id);
    if (result) {
      setDetail(null);
      await load();
    }
    setPendingTransition(null);
  };

  const transitionCopy = {
    ship: {
      title: "Kirim stok sekarang?",
      message: `Stok pada ${pendingTransition?.transfer?.transfer_no || "transfer ini"} akan dikurangi dari outlet asal dan masuk status dalam pengiriman.`,
      confirmText: "Kirim stok",
      type: "warning",
    },
    receive: {
      title: "Terima stok transfer?",
      message: `Pastikan jumlah fisik ${pendingTransition?.transfer?.transfer_no || "transfer ini"} sudah sesuai sebelum menambah stok outlet tujuan.`,
      confirmText: "Terima stok",
      type: "warning",
    },
    cancel: {
      title: "Batalkan transfer?",
      message: `Transfer ${pendingTransition?.transfer?.transfer_no || "ini"} akan dibatalkan. Jika stok sudah dikirim, sistem akan membuat reversal pada ledger.`,
      confirmText: "Batalkan",
      type: "error",
    },
  };
  const confirmation = transitionCopy[pendingTransition?.action] || transitionCopy.ship;

  const summary = useMemo(() => ({
    draft: rows.filter((row) => row.status === "draft").length,
    incoming: rows.filter((row) => row.status === "shipped" && Number(row.destination_outlet_id) === activeOutletId).length,
    sent: rows.filter((row) => row.status === "shipped" && Number(row.source_outlet_id) === activeOutletId).length,
  }), [activeOutletId, rows]);

  return (
    <div className="page-wrapper stock-transfer-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center g-3">
            <div className="col">
              <div className="page-pretitle">PERGERAKAN ANTAR OUTLET</div>
              <h2 className="page-title">Transfer Stok</h2>
              <p className="text-secondary mb-0">
                Pengiriman dan penerimaan menggunakan ledger yang sama agar audit stok tetap utuh.
              </p>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" type="button" onClick={openCreate} disabled={optionsLoading}>
                {optionsLoading ? "Menyiapkan..." : "+ Buat Transfer"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="transfer-summary">
            <div><small>Draft</small><strong>{summary.draft}</strong></div>
            <div><small>Menunggu diterima</small><strong>{summary.sent}</strong></div>
            <div className="is-highlight"><small>Perlu diterima</small><strong>{summary.incoming}</strong></div>
          </div>

          <div className="card transfer-table-card">
            <div className="card-header transfer-toolbar">
              <div>
                <h3 className="card-title">Daftar transfer</h3>
                <span className="text-secondary small">{meta.total || rows.length} dokumen</span>
              </div>
              <div className="transfer-filters">
                <input
                  className="form-control"
                  placeholder="Cari nomor atau outlet..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <AkasSelect className="form-select" value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">Semua status</option>
                  <option value="draft">Draft</option>
                  <option value="shipped">Dikirim</option>
                  <option value="received">Diterima</option>
                  <option value="cancelled">Dibatalkan</option>
                </AkasSelect>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter">
                <thead><tr><th>Nomor transfer</th><th>Rute outlet</th><th>Status</th><th>Item</th><th>Waktu</th><th /></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5 text-secondary">Memuat transfer...</td></tr>
                  ) : rows.length ? rows.map((row) => {
                    const state = STATUS[row.status] || STATUS.draft;
                    return (
                      <tr key={row.id}>
                        <td><strong>{row.transfer_no}</strong><small className="d-block text-secondary">{row.created_by_name || "-"}</small></td>
                        <td><span>{row.source_outlet_name}</span><span className="transfer-route-arrow">→</span><strong>{row.destination_outlet_name}</strong></td>
                        <td><span className={`badge ${state.className}`}>{state.label}</span></td>
                        <td>{row.total_items} item</td>
                        <td>{new Date(row.created_at).toLocaleString("id-ID")}</td>
                        <td className="text-end"><button type="button" className="btn btn-sm btn-primary" onClick={() => openDetail(row.id)}>Detail</button></td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center py-5 text-secondary">Belum ada transfer stok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showForm ? (
        <div className="transfer-backdrop">
          <div className="transfer-modal">
            <form onSubmit={save}>
              <header>
                <div><small>TRANSFER BARU</small><h3>Kirim stok ke outlet lain</h3></div>
                <button className="btn-close" type="button" onClick={() => setShowForm(false)} aria-label="Tutup" />
              </header>
              <main>
                <FormValidationNotice
                  errors={validationAttempted ? {
                    ...(!form.destination_outlet_id ? { destination_outlet_id: "Outlet tujuan wajib dipilih." } : {}),
                    ...form.details.reduce((result, line, index) => {
                      const item = selectedItem(line);
                      const quantity = Number(line.qty);
                      if (!line.item_id) result[`details.${index}.item_id`] = "Item wajib dipilih.";
                      if (!Number.isFinite(quantity) || quantity <= 0) result[`details.${index}.qty`] = "Jumlah harus lebih dari 0.";
                      else if (line.item_type === "product" && !Number.isInteger(quantity)) result[`details.${index}.qty`] = "Jumlah produk harus bilangan bulat.";
                      else if (quantity > Number(item?.stock || 0)) result[`details.${index}.qty`] = "Jumlah melebihi stok outlet.";
                      return result;
                    }, {}),
                  } : {}}
                  title="Lengkapi transfer stok yang ditandai."
                />
                {optionsLoading ? (
                  <div className="transfer-options-loading">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    Menyiapkan stok outlet dan referensi satuan...
                  </div>
                ) : (
                  <>
                    <label className="form-label">Outlet tujuan<RequiredMark /></label>
                    <AkasSelect
                      required
                      className={`form-select${validationAttempted && !form.destination_outlet_id ? " is-invalid" : ""}`}
                      value={form.destination_outlet_id}
                      onChange={(event) => setForm((current) => ({ ...current, destination_outlet_id: event.target.value }))}>
                      <option value="">Pilih outlet tujuan</option>
                      {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}
                    </AkasSelect>
                    {validationAttempted && !form.destination_outlet_id ? <div className="invalid-feedback d-block mb-3">Outlet tujuan wajib dipilih.</div> : null}

                    <div className="transfer-line-heading">
                      <div>
                        <strong>Item yang dikirim</strong>
                        <small>Jumlah menggunakan satuan dasar. Stok baru berkurang saat transfer dikirim.</small>
                      </div>
                      <button type="button" className="btn btn-sm btn-primary" onClick={addLine}>+ Item</button>
                    </div>

                    <div className="transfer-lines">
                      {form.details.map((line, index) => {
                        const item = selectedItem(line);
                        const unit = item?.stock_unit_label || item?.stock_unit || "-";
                        return (
                          <div className="transfer-line" key={index}>
                            <label className="transfer-field">
                              <span>Jenis</span>
                              <AkasSelect className="form-select" value={line.item_type} onChange={(event) => updateLine(index, "item_type", event.target.value)}>
                                <option value="product">Produk</option>
                                <option value="material">Material</option>
                              </AkasSelect>
                            </label>
                            <label className="transfer-field">
                              <span>Item <RequiredMark /></span>
                              <AkasSelect required className={`form-select${validationAttempted && !line.item_id ? " is-invalid" : ""}`} value={line.item_id} onChange={(event) => updateLine(index, "item_id", event.target.value)}>
                                <option value="">Pilih item</option>
                                {itemOptions(line.item_type).map((option) => (
                                  <option
                                    key={option.id}
                                    value={option.id}
                                    disabled={form.details.some((otherLine, otherIndex) => (
                                      otherIndex !== index
                                      && otherLine.item_type === line.item_type
                                      && Number(otherLine.item_id) === Number(option.id)
                                    ))}>
                                    {option.name} · {formatQuantity(option.stock)} {option.stock_unit_label}
                                  </option>
                                ))}
                              </AkasSelect>
                              {validationAttempted && !line.item_id ? <span className="invalid-feedback d-block">Item wajib dipilih.</span> : null}
                            </label>
                            <div className={`transfer-stock-info ${item && item.stock <= 0 ? "is-empty" : ""}`}>
                              <span>Stok outlet saat ini</span>
                              <strong>{item ? formatQuantity(item.stock) : "-"} <small>{unit}</small></strong>
                            </div>
                            <label className="transfer-field transfer-qty-field">
                              <span>Jumlah dikirim <RequiredMark /></span>
                              <div className="input-group">
                                <NumberInput
                                  required
                                  min={line.item_type === "material" ? "0.0001" : "1"}
                                  max={item ? item.stock : undefined}
                                  step={line.item_type === "material" ? "0.0001" : "1"}
                                  type="number"
                                  className={`form-control${validationAttempted && (
                                    !Number.isFinite(Number(line.qty)) || Number(line.qty) <= 0 ||
                                    Number(line.qty) > Number(item?.stock || 0) ||
                                    (line.item_type === "product" && !Number.isInteger(Number(line.qty)))
                                  ) ? " is-invalid" : ""}`}
                                  value={line.qty}
                                  onChange={(event) => updateLine(index, "qty", event.target.value)}
                                  aria-label={`Jumlah transfer dalam ${unit}`}
                                />
                                <span className="input-group-text">{unit}</span>
                              </div>
                              {validationAttempted && (!Number.isFinite(Number(line.qty)) || Number(line.qty) <= 0) ? <span className="invalid-feedback d-block">Jumlah harus lebih dari 0.</span> : null}
                              {validationAttempted && line.item_type === "product" && Number(line.qty) > 0 && !Number.isInteger(Number(line.qty)) ? <span className="invalid-feedback d-block">Jumlah produk harus bilangan bulat.</span> : null}
                              {validationAttempted && item && Number(line.qty) > Number(item.stock) ? <span className="invalid-feedback d-block">Jumlah melebihi stok outlet saat ini.</span> : null}
                            </label>
                            {form.details.length > 1 ? (
                              <button
                                type="button"
                                className="btn btn-icon btn-outline-danger transfer-remove-line"
                                onClick={() => removeLine(index)}
                                aria-label={`Hapus item transfer ${index + 1}`}>
                                ×
                              </button>
                            ) : <span className="transfer-remove-placeholder" />}
                          </div>
                        );
                      })}
                    </div>

                    <label className="form-label mt-3">Catatan</label>
                    <textarea
                      className="form-control"
                      maxLength="1000"
                      rows="2"
                      placeholder="Contoh: restock operasional outlet cabang"
                      value={form.note}
                      onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
                    />
                  </>
                )}
              </main>
              <footer>
                <button type="button" className="btn btn-link" onClick={() => setShowForm(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading || optionsLoading}>
                  {actionLoading ? "Menyimpan..." : "Simpan draft"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {detail ? (
        <div className="transfer-backdrop">
          <div className="transfer-modal transfer-detail">
            <header>
              <div><small>DETAIL TRANSFER</small><h3>{detail.transfer_no}</h3></div>
              <button className="btn-close" type="button" onClick={() => setDetail(null)} aria-label="Tutup" />
            </header>
            <main>
              <div className="transfer-route-card">
                <div><small>Dari</small><strong>{detail.source_outlet_name}</strong></div>
                <span>→</span>
                <div><small>Tujuan</small><strong>{detail.destination_outlet_name}</strong></div>
              </div>
              <div className="table-responsive">
                <table className="table">
                  <thead><tr><th>Item</th><th>Tipe</th><th className="text-end">Diminta</th><th className="text-end">Dikirim</th><th className="text-end">Diterima</th></tr></thead>
                  <tbody>
                    {(detail.details || []).map((line) => (
                      <tr key={line.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ItemAvatar
                              name={line.item_name}
                              image={line?.image_path || line?.image}
                              size="sm"
                            />
                            <div>
                              <strong>{line.item_name}</strong>
                              <small className="d-block text-secondary">Satuan: {line.unit}</small>
                            </div>
                          </div>
                        </td>
                        <td>{line.item_type === "material" ? "Material" : "Produk"}</td>
                        <td className="text-end"><strong>{formatQuantity(line.requested_qty)}</strong><small className="d-block text-secondary">{line.unit}</small></td>
                        <td className="text-end"><strong>{formatQuantity(line.shipped_qty)}</strong><small className="d-block text-secondary">{line.unit}</small></td>
                        <td className="text-end"><strong>{formatQuantity(line.received_qty)}</strong><small className="d-block text-secondary">{line.unit}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detail.note ? <div className="alert alert-info mb-0">{detail.note}</div> : null}
            </main>
            <footer>
              <span className={`badge ${(STATUS[detail.status] || STATUS.draft).className}`}>
                {(STATUS[detail.status] || STATUS.draft).label}
              </span>
              <div className="ms-auto d-flex gap-2">
                {detail.status === "draft" && Number(detail.source_outlet_id) === activeOutletId ? (
                  <>
                    {hasAnyPermission(userProfile, ["edit-stock-transfers"]) ? (<button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={actionLoading}
                      data-bs-toggle="modal"
                      data-bs-target="#modal-alert-stock-transfer-action"
                      onClick={() => setPendingTransition({ transfer: detail, action: "cancel" })}>
                      Batalkan
                    </button>) : null}
                    {canProcess ? (<button
                      type="button"
                      className="btn btn-primary"
                      disabled={actionLoading}
                      data-bs-toggle="modal"
                      data-bs-target="#modal-alert-stock-transfer-action"
                      onClick={() => setPendingTransition({ transfer: detail, action: "ship" })}>
                      Kirim stok
                    </button>) : null}
                  </>
                ) : null}
                {canProcess && detail.status === "shipped" && Number(detail.destination_outlet_id) === activeOutletId ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={actionLoading}
                    data-bs-toggle="modal"
                    data-bs-target="#modal-alert-stock-transfer-action"
                    onClick={() => setPendingTransition({ transfer: detail, action: "receive" })}>
                    Terima stok
                  </button>
                ) : null}
              </div>
            </footer>
          </div>
        </div>
      ) : null}

      <AlertConfirmModal
        type={confirmation.type}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText="Kembali"
        idModal="alert-stock-transfer-action"
        onConfirm={transition}
        onCancel={() => setPendingTransition(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userProfile: state?.auth?.login?.profile,
  dataStockTransfers: state?.stockTransfers?.datas,
  loading: state?.stockTransfers?.loading,
  actionLoading: state?.stockTransfers?.actionLoading,
});

export default connect(mapStateToProps, {
  fetchStockTransfers,
  getStockTransferById,
  fetchStockTransferOptions,
  addStockTransfer,
  shipStockTransfer,
  receiveStockTransfer,
  cancelStockTransfer,
})(StockTransferPage);
